import { observer } from "mobx-react-lite";
import { v4 as uuid } from "uuid";
import type { NodeProps } from "@xyflow/react";
import { AddActionButton } from "../../AddActionButton";
import { SmallAddActionButton } from "../../SmallAddActionButton";
import { useRootStore } from "../../../stores/hooks";
import { useFlowCanvasHandlers } from "./context";
import { HiddenHandles } from "./handles";
import { NodeSectionLabel } from "./NodeSectionLabel";
import type { FlowNodeData } from "../graph/types";
import { SECTION_LABEL_HEIGHT } from "../graph/constants";
import type { BranchRef } from "../../../stores/automation/branches.store";
import type { ActionGroupCandidate } from "../../../types/candidate";
import * as A from "../../../stores/automation/mergers/actions";
import { capitalize } from "../../../locales/i18n";
import { t } from "i18next";

/**
 * React Flow wrapper over the legacy add-action sites. Root list uses the same
 * `AddActionButton` as Sidebar/index.tsx (big dashed box when empty); loop and
 * branch columns keep `SmallAddActionButton`.
 */
export const AddActionNode = observer<NodeProps>(({ data }) => {
  const { locator, sectionLabel } = data as Extract<
    FlowNodeData,
    { kind: "addAction" }
  >;
  const store = useRootStore();
  const handlers = useFlowCanvasHandlers();
  const candidate = store.automations.candidate;
  const branchesStore = candidate.branches;

  const branchRef: BranchRef | undefined =
    locator.kind === "branch"
      ? { instanceId: locator.instanceId, branchHash: locator.branchHash }
      : undefined;

  const triggerId = candidate.self.trigger.triggerReference?.triggerId ?? "";
  const enabled =
    triggerId !== "" &&
    candidate.totalStepCount <
      store.featureFlags.limits.maxActionsPerAutomation;

  const advancedLogic =
    branchRef !== undefined
      ? {
          looping: branchesStore.canAddLoopAt(branchRef),
          branches: branchesStore.canAddBlockAt(branchRef),
        }
      : locator.kind === "loop"
        ? {
            looping: branchesStore.canAddLoopInLoop(locator.instanceId),
            branches: branchesStore.canAddBlockInLoop(locator.instanceId),
          }
        : {
            looping: !candidate.self.actionGroups.some(
              (group) => group.config.oneofKind === "loop",
            ),
            branches: !branchesStore.hasBranches,
          };

  const handleClick = (integrationId: string, actionId: string): void => {
    if (integrationId === "branches") {
      if (branchRef !== undefined) {
        const id = branchesStore.addBlockAt(branchRef);
        if (id !== undefined) handlers.onSelectBlock(id);
        return;
      }
      if (locator.kind === "loop") {
        const id = branchesStore.addBlockInLoop(locator.instanceId);
        if (id !== undefined) handlers.onSelectBlock(id);
        return;
      }
      handlers.onSelectBlock(branchesStore.addBlock());
      return;
    }

    if (integrationId === "looping") {
      if (branchRef !== undefined) {
        const id = branchesStore.addLoopAt(branchRef);
        if (id !== undefined) handlers.onSelectNestedLoop(id);
        return;
      }
      if (locator.kind === "loop") {
        const id = branchesStore.addLoopInLoop(locator.instanceId);
        if (id !== undefined) handlers.onSelectNestedLoop(id);
        return;
      }
      candidate.looping.setActionGroups([
        A.addLoopActionGroup(
          actionId as ActionGroupCandidate["config"]["oneofKind"],
        ),
      ]);
      return;
    }

    const hash = uuid();
    candidate.setActionsAt(locator, [
      A.mergeEmptyAction(hash, integrationId, actionId),
    ]);
    handlers.onClickAction(hash);
  };

  // totalStepCount, not allActions: a Conditional Logic / Looping block is a
  // step with no leaf actions, and its empty branches raise their own errors
  const showEmptyActionError =
    candidate.isTriggerSubmitted && candidate.totalStepCount === 0;

  const isRootAddSite = locator.kind === "root";
  const rootActions = candidate.groupActions ?? [];

  return (
    <>
      <HiddenHandles
        targetTopOffset={
          sectionLabel !== undefined ? SECTION_LABEL_HEIGHT : undefined
        }
      />
      <NodeSectionLabel label={sectionLabel} />
      {isRootAddSite ? (
        <AddActionButton
          enabled={enabled}
          bigSize={rootActions.length === 0 && !branchesStore.hasBranches}
          label={capitalize(t("automation-form.add-action"))}
          showError={showEmptyActionError}
          setActiveSection={(item) => {
            if (item === null) return;
            if (item.type === "trigger") handlers.onClickTrigger();
            else if (item.type === "action") handlers.onClickAction(item.hash);
            else if (item.type === "looping")
              handlers.onSelectLooping(item.hash);
          }}
          onClick={handleClick}
        />
      ) : (
        <SmallAddActionButton
          fullWidth={branchRef !== undefined}
          disabled={!enabled}
          advancedLogic={advancedLogic}
          onClick={handleClick}
        />
      )}
    </>
  );
});
AddActionNode.displayName = "AddActionNode";
